'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import Modal from '@/components/ui/Modal';
import { createClient } from '@/lib/supabase/client';

interface LiveCounter {
  id: string;
  label: string;
  value: string;
  suffix: string;
  color: string;
  icon: string;
  glow: string;
}

const PIPELINE_STAGES_LABELS = [
  'Requirement', 'RFQ Sent', 'Vendor Response', 'Margin Review', 'Quote Sent', 'Won',
];

export default function DashboardPage() {
  const [mounted, setMounted] = useState(false);
  const [counters, setCounters] = useState<LiveCounter[]>([]);
  const [pipelineHealth, setPipelineHealth] = useState(0);
  const [pipelineStages, setPipelineStages] = useState<{ label: string; count: number; pct: number }[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [atRisk, setAtRisk] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showAIReportModal, setShowAIReportModal] = useState(false);
  const [aiReportGenerating, setAiReportGenerating] = useState(false);
  const [aiReport, setAiReport] = useState('');
  const [rawData, setRawData] = useState<any>({});

  useEffect(() => {
    setMounted(true);
    const fetchDashboard = async () => {
      const supabase = createClient();
      try {
        const [{ data: proposals }, { data: approvals }, { data: activities }] = await Promise.all([
          supabase.from('proposals').select('id, category, value_amount, stage, deadline_at, blocker, code, title, customer_name, owner_name'),
          supabase.from('approvals').select('id, status'),
          supabase.from('activity_logs').select('id, user_name, action, target, created_at').order('created_at', { ascending: false }).limit(5),
        ]);

        const all = proposals || [];
        const active = all.filter((p: any) => !['won', 'lost'].includes(p.category));
        const won = all.filter((p: any) => p.category === 'won');
        const lost = all.filter((p: any) => p.category === 'lost');
        const pipeline = active.reduce((sum: number, p: any) => sum + Number(p.value_amount || 0), 0);
        const convRate = (won.length + lost.length) > 0 ? (won.length / (won.length + lost.length)) * 100 : 0;
        const pendingApprovals = (approvals || []).filter((a: any) => a.status === 'pending').length;
        const pipelineLabel = pipeline >= 1000000 ? `${(pipeline / 1000000).toFixed(1)}` : `${(pipeline / 1000).toFixed(1)}`;
        const pipelineSuffix = pipeline >= 1000000 ? 'M OMR' : 'K OMR';

        setRawData({ all, active, won, lost, pipeline, convRate, pendingApprovals });

        setCounters([
          { id: 'c1', label: 'Active Missions', value: String(active.length), suffix: '', color: '#1E90FF', icon: 'RocketLaunchIcon', glow: 'rgba(30,144,255,0.3)' },
          { id: 'c2', label: 'Pipeline Value', value: pipelineLabel, suffix: pipelineSuffix, color: '#00D68F', icon: 'BanknotesIcon', glow: 'rgba(0,214,143,0.3)' },
          { id: 'c3', label: 'Won This Month', value: String(won.length), suffix: '', color: '#F59E0B', icon: 'TrophyIcon', glow: 'rgba(245,158,11,0.3)' },
          { id: 'c4', label: 'Conversion Rate', value: convRate.toFixed(1), suffix: '%', color: '#00D68F', icon: 'ArrowTrendingUpIcon', glow: 'rgba(0,214,143,0.3)' },
          { id: 'c5', label: 'Pending Approvals', value: String(pendingApprovals), suffix: '', color: '#FF4757', icon: 'ShieldExclamationIcon', glow: 'rgba(255,71,87,0.3)' },
          { id: 'c6', label: 'Total Proposals', value: String(all.length), suffix: '', color: '#1E90FF', icon: 'CalendarDaysIcon', glow: 'rgba(30,144,255,0.3)' },
        ]);

        const stageMap: Record<string, number> = {};
        active.forEach((p: any) => { stageMap[p.stage] = (stageMap[p.stage] || 0) + 1; });
        const maxCount = Math.max(...Object.values(stageMap), 1);
        const stagesData = PIPELINE_STAGES_LABELS.map(label => {
          const count = stageMap[label] || 0;
          return { label, count, pct: Math.round((count / maxCount) * 100) };
        });
        setPipelineStages(stagesData);

        const health = active.length > 0 ? Math.min(100, Math.round((won.length / Math.max(all.length, 1)) * 100 + (active.length / Math.max(all.length, 1)) * 50)) : 0;
        setPipelineHealth(health);

        const now = Date.now();
        const risks = all
          .filter((p: any) => p.blocker || (p.deadline_at && new Date(p.deadline_at).getTime() - now < 7 * 24 * 3600000 && !['won', 'lost'].includes(p.category)))
          .slice(0, 3)
          .map((p: any) => {
            const deadlineDiff = p.deadline_at ? (new Date(p.deadline_at).getTime() - now) / (24 * 3600000) : 999;
            const urgency = deadlineDiff < 2 || p.blocker ? 'critical' : deadlineDiff < 5 ? 'high' : 'medium';
            const val = Number(p.value_amount);
            return {
              id: p.id,
              code: p.code,
              title: p.title,
              risk: p.blocker || (deadlineDiff < 0 ? 'Overdue' : `Due in ${Math.ceil(deadlineDiff)}d`),
              urgency,
              value: val >= 1000 ? `${(val / 1000).toFixed(0)}K OMR` : `${val} OMR`,
              deadline: p.deadline_at ? `${Math.ceil(Math.max(0, deadlineDiff))}d` : '—',
            };
          });
        setAtRisk(risks);

        setRecentActivity((activities || []).map((a: any) => {
          const diff = Date.now() - new Date(a.created_at).getTime();
          const mins = Math.floor(diff / 60000);
          const hours = Math.floor(mins / 60);
          const time = hours > 0 ? `${hours} hr ago` : `${mins} min ago`;
          return { id: a.id, msg: `${a.user_name} — ${a.action} ${a.target}`, time, type: 'info' };
        }));
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const handleExportIntel = async () => {
    setExporting(true);
    await new Promise(r => setTimeout(r, 400));
    const rows = [
      ['ProposalLink — Executive Intel Report'],
      [`Generated: ${new Date().toLocaleString()}`],
      [''],
      ['METRIC', 'VALUE'],
      ['Total Proposals', rawData.all?.length || 0],
      ['Active Proposals', rawData.active?.length || 0],
      ['Won Proposals', rawData.won?.length || 0],
      ['Lost Proposals', rawData.lost?.length || 0],
      ['Pipeline Value (OMR)', rawData.pipeline || 0],
      ['Conversion Rate (%)', rawData.convRate ? rawData.convRate.toFixed(1) : 0],
      ['Pending Approvals', rawData.pendingApprovals || 0],
      ['Pipeline Health (%)', pipelineHealth],
      [''],
      ['AT-RISK PROPOSALS'],
      ['Code', 'Title', 'Risk', 'Value', 'Deadline'],
      ...atRisk.map((r: any) => [r.code, r.title, r.risk, r.value, r.deadline]),
    ];
    const csv = rows.map(r => (Array.isArray(r) ? r.join(',') : r)).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proposallink-intel-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExporting(false);
  };

  const handleGenerateAIReport = async () => {
    setAiReportGenerating(true);
    setAiReport('');
    await new Promise(r => setTimeout(r, 1200));
    const all = rawData.all || [];
    const active = rawData.active || [];
    const won = rawData.won || [];
    const convRate = rawData.convRate || 0;
    const pipeline = rawData.pipeline || 0;
    const report = `## Executive AI Briefing — ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}

**Portfolio Overview**
You currently have ${all.length} total proposals with ${active.length} active missions in the pipeline. The total pipeline value stands at ${pipeline >= 1000000 ? (pipeline / 1000000).toFixed(1) + 'M' : (pipeline / 1000).toFixed(0) + 'K'} OMR with a conversion rate of ${convRate.toFixed(1)}%.

**Priority Actions**
${atRisk.length > 0 ? atRisk.map((r: any, i: number) => `${i + 1}. **${r.code}** — ${r.risk} (${r.value})`).join('\n') : '✅ No critical actions required at this time.'}

**Pipeline Health: ${pipelineHealth}%**
${pipelineHealth >= 70 ? '🟢 Pipeline is healthy. Focus on closing active deals.' : pipelineHealth >= 50 ? '🟡 Pipeline needs attention. Review stalled proposals.' : '🔴 Pipeline health is low. Immediate action required on at-risk proposals.'}

**Recommendations**
${won.length > 0 ? `• ${won.length} proposals won — document success patterns for future bids.` : '• Focus on moving active proposals to the closing stage.'}
${rawData.pendingApprovals > 0 ? `• ${rawData.pendingApprovals} approvals pending — follow up with approvers to avoid delays.` : '• Approval chain is clear.'}
• Review vendor response rates and send reminders for overdue RFQs.`;
    setAiReport(report);
    setAiReportGenerating(false);
  };

  const URGENCY_COLORS: Record<string, string> = { critical: '#FF4757', high: '#F59E0B', medium: '#1E90FF' };

  return (
    <AppLayout pageTitle="Mission Map — Executive HQ">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-700 tracking-widest uppercase px-2 py-0.5 rounded" style={{ background: 'rgba(30,144,255,0.15)', color: '#1E90FF', border: '1px solid rgba(30,144,255,0.3)' }}>◆ EXECUTIVE HQ</span>
            <span className="text-[10px] font-600 px-2 py-0.5 rounded" style={{ background: 'rgba(0,214,143,0.1)', color: '#00D68F', border: '1px solid rgba(0,214,143,0.2)' }}>● LIVE</span>
          </div>
          <h1 className="page-title" style={{ color: 'var(--card-foreground)' }}>Mission Map</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>ProposalLink War Room · Live data</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportIntel} disabled={exporting || loading} className="btn-ghost text-sm flex items-center gap-1.5">
            <Icon name="ArrowDownTrayIcon" size={14} />{exporting ? 'Exporting...' : 'Export Intel'}
          </button>
          <Link href="/proposal-control-room">
            <button className="btn-primary text-sm flex items-center gap-1.5"><Icon name="RocketLaunchIcon" size={14} />Launch Mission</button>
          </Link>
        </div>
      </div>

      {/* Live Counters Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
        {loading ? Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-xl p-4 animate-pulse" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.15)' }}>
            <div className="w-8 h-8 rounded-lg bg-muted mb-3" />
            <div className="h-7 w-16 bg-muted rounded mb-2" />
            <div className="h-3 w-20 bg-muted rounded" />
          </div>
        )) : counters.map((c) => (
          <div key={c.id} className="rounded-xl p-4 card-hover cursor-pointer" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.15)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: c.glow, border: `1px solid ${c.color}30` }}>
                <Icon name={c.icon as any} size={16} style={{ color: c.color } as React.CSSProperties} />
              </div>
              <span className="text-[9px] font-700 tracking-widest uppercase" style={{ color: c.color }}>LIVE</span>
            </div>
            <p className="text-2xl font-700 font-tabular leading-none" style={{ color: 'var(--card-foreground)' }}>{c.value}{c.suffix}</p>
            <p className="text-[10px] mt-1.5 font-500 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>{c.label}</p>
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        {/* Pipeline Health */}
        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.15)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs font-700 uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Pipeline Health</p>
              <p className="text-lg font-700 mt-0.5" style={{ color: 'var(--card-foreground)' }}>Portfolio Status</p>
            </div>
            <div className="text-2xl font-700 font-tabular" style={{ color: pipelineHealth >= 70 ? '#00D68F' : pipelineHealth >= 50 ? '#F59E0B' : '#FF4757' }}>{pipelineHealth}%</div>
          </div>
          <div className="relative h-3 rounded-full mb-4 overflow-hidden" style={{ background: 'var(--muted)' }}>
            <div className="h-full rounded-full transition-all duration-1000" style={{ width: mounted ? `${pipelineHealth}%` : '0%', background: 'linear-gradient(90deg, #FF4757 0%, #F59E0B 40%, #00D68F 70%, #1E90FF 100%)' }} />
          </div>
          <div className="space-y-2">
            {pipelineStages.map((s) => (
              <div key={s.label} className="flex items-center gap-2">
                <span className="text-[10px] w-28 shrink-0" style={{ color: 'var(--muted-foreground)' }}>{s.label}</span>
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
                  <div className="h-full rounded-full" style={{ width: mounted ? `${s.pct}%` : '0%', background: 'linear-gradient(90deg, #1E90FF, #00D68F)', transition: 'width 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)' }} />
                </div>
                <span className="text-[10px] font-600 w-4 text-right" style={{ color: 'var(--card-foreground)' }}>{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* AI Briefing */}
        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}>
              <Icon name="SparklesIcon" size={16} style={{ color: '#F59E0B' } as React.CSSProperties} />
            </div>
            <div>
              <p className="text-xs font-700 uppercase tracking-widest" style={{ color: '#F59E0B' }}>AI Briefing</p>
              <p className="text-sm font-600" style={{ color: 'var(--card-foreground)' }}>Today's Priority Actions</p>
            </div>
          </div>
          <div className="space-y-3">
            {atRisk.slice(0, 3).map((r, i) => (
              <div key={r.id} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: i === 0 ? 'rgba(255,71,87,0.08)' : i === 1 ? 'rgba(245,158,11,0.08)' : 'rgba(30,144,255,0.08)', border: `1px solid ${i === 0 ? 'rgba(255,71,87,0.2)' : i === 1 ? 'rgba(245,158,11,0.2)' : 'rgba(30,144,255,0.2)'}` }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-700 shrink-0 mt-0.5 text-white" style={{ background: i === 0 ? '#FF4757' : i === 1 ? '#F59E0B' : '#1E90FF' }}>{i + 1}</span>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--card-foreground)' }}>{r.code} — {r.risk}</p>
              </div>
            ))}
            {atRisk.length === 0 && !loading && (
              <div className="text-center py-4" style={{ color: 'var(--muted-foreground)' }}>
                <Icon name="CheckCircleIcon" size={20} className="mx-auto mb-1 opacity-40" />
                <p className="text-xs">No urgent actions needed</p>
              </div>
            )}
          </div>
          <button
            onClick={() => { setShowAIReportModal(true); setAiReport(''); }}
            className="w-full mt-4 py-2 rounded-lg text-xs font-600 transition-all flex items-center justify-center gap-1.5"
            style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.2)' }}
          >
            <Icon name="SparklesIcon" size={12} />Generate Full AI Report
          </button>
        </div>

        {/* Recent Activity */}
        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid rgba(30,144,255,0.15)' }}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-600" style={{ color: 'var(--card-foreground)' }}>Recent Activity</p>
            <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>Live</span>
          </div>
          <div className="space-y-3">
            {loading ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2 animate-pulse">
                <div className="w-6 h-6 rounded-full bg-muted shrink-0" />
                <div className="flex-1"><div className="h-3 bg-muted rounded w-3/4 mb-1" /><div className="h-2 bg-muted rounded w-1/4" /></div>
              </div>
            )) : recentActivity.map((a) => (
              <div key={a.id} className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: a.type === 'success' ? '#00D68F' : a.type === 'warning' ? '#F59E0B' : '#1E90FF' }} />
                <div>
                  <p className="text-xs" style={{ color: 'var(--card-foreground)' }}>{a.msg}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{a.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* At-Risk Missions */}
      <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid rgba(255,71,87,0.2)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <p className="text-sm font-700" style={{ color: 'var(--card-foreground)' }}>At-Risk Missions</p>
          </div>
          <span className="text-[10px] font-600 px-2 py-0.5 rounded" style={{ background: 'rgba(255,71,87,0.15)', color: '#FF4757' }}>{atRisk.length} CRITICAL</span>
        </div>
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />)}</div>
        ) : atRisk.length === 0 ? (
          <div className="text-center py-6" style={{ color: 'var(--muted-foreground)' }}>
            <Icon name="CheckCircleIcon" size={24} className="mx-auto mb-2 opacity-30" />
            <p className="text-xs">No at-risk proposals right now</p>
          </div>
        ) : (
          <div className="space-y-2">
            {atRisk.map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.15)' }}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: URGENCY_COLORS[r.urgency] }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-700" style={{ color: '#1E90FF' }}>{r.code}</span>
                    <span className="text-xs font-500 truncate" style={{ color: 'var(--card-foreground)' }}>{r.title}</span>
                  </div>
                  <p className="text-[10px] mt-0.5" style={{ color: '#FF4757' }}>{r.risk}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-700" style={{ color: 'var(--card-foreground)' }}>{r.value}</p>
                  <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>Due {r.deadline}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Report Modal */}
      <Modal open={showAIReportModal} onClose={() => setShowAIReportModal(false)} title="AI Executive Report" size="lg">
        <div className="space-y-4">
          {!aiReport && !aiReportGenerating && (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <Icon name="SparklesIcon" size={28} style={{ color: '#F59E0B' } as React.CSSProperties} />
              </div>
              <p className="text-sm font-600" style={{ color: 'var(--card-foreground)' }}>Generate AI Executive Briefing</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Analyzes your live pipeline data and generates actionable insights</p>
              <button onClick={handleGenerateAIReport} className="mt-4 btn-primary text-sm px-6 py-2.5 flex items-center gap-2 mx-auto">
                <Icon name="SparklesIcon" size={15} />Generate Report
              </button>
            </div>
          )}
          {aiReportGenerating && (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Analyzing pipeline data...</p>
            </div>
          )}
          {aiReport && (
            <div className="space-y-3">
              <div className="rounded-xl p-4 text-sm leading-relaxed whitespace-pre-line overflow-y-auto max-h-80" style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.15)', color: 'var(--card-foreground)', fontFamily: 'inherit' }}>
                {aiReport}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const blob = new Blob([aiReport], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `ai-report-${new Date().toISOString().slice(0, 10)}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="btn-secondary text-xs px-4 py-2 flex items-center gap-1.5"
                >
                  <Icon name="ArrowDownTrayIcon" size={13} />Download Report
                </button>
                <button onClick={handleGenerateAIReport} className="btn-ghost text-xs px-4 py-2 flex items-center gap-1.5">
                  <Icon name="ArrowPathIcon" size={13} />Regenerate
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </AppLayout>
  );
}